import { useCallback, useEffect, useState } from 'react';
import CiselnikEditor from '../layout/CiselnikEditor';

/**
 * Číselníky pro sekce ⚙ Nastavení modulů (norma §1h). Nahrazují záložky
 * zrušené `Konfigurace.jsx` (revize Nastavení, 2026-10-12) — stejná API,
 * tvar podle normy (CiselnikEditor). Každý číselník je tady jednou, modul
 * si ho jen vloží do své sekce.
 */
const nactiJson = (url) => fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : [])).catch(() => []);
const poradi = (a, b) => (a.poradi ?? 0) - (b.poradi ?? 0) || String(a.nazev).localeCompare(String(b.nazev), 'cs');
const kc = (v) => (v == null || v === '' ? '—' : `${Number(v).toLocaleString('cs-CZ')} Kč`);

export function TypyUvazku(p) {
    return <CiselnikEditor {...p} endpoint="/api/konfigurace/typy-uvazku" muzePridat={false} muzeMazat={false} nazevPolozky="typ úvazku"
        popis="Koeficient odvodů násobí hrubou mzdu (náklad firmy), limit = do kolika Kč měsíčně se neodvádí (DPP/DPČ)."
        titulek={(r) => r.nazev} meta={(r) => `koeficient ${r.koef_odvody} · limit ${kc(r.limit_bez_odvodu_kc)}`}
        pole={[
            { klic: 'koef_odvody', nazev: 'Koeficient odvodů', typ: 'cislo', krok: '0.001', povinne: true, napoveda: 'např. 1,338 = mzda + 33,8 % odvodů' },
            { klic: 'limit_bez_odvodu_kc', nazev: 'Limit bez odvodů', typ: 'cislo', pripona: 'Kč / měsíc' },
        ]} />;
}

export function Pozice(p) {
    return <CiselnikEditor {...p} endpoint="/api/konfigurace/pozice" nazevPolozky="pozici" razeni={poradi}
        pole={[{ klic: 'nazev', nazev: 'Název', typ: 'text', povinne: true, siroke: true }]} />;
}

export function Oddeleni(p) {
    return <CiselnikEditor {...p} endpoint="/api/konfigurace/oddeleni" nazevPolozky="oddělení" razeni={poradi}
        pole={[{ klic: 'nazev', nazev: 'Název', typ: 'text', povinne: true, siroke: true }]} />;
}

export function Pojistovny(p) {
    return <CiselnikEditor {...p} endpoint="/api/konfigurace/pojistovny" nazevPolozky="pojišťovnu" razeni={poradi}
        titulek={(r) => `${r.kod} · ${r.nazev}`}
        pole={[
            { klic: 'kod', nazev: 'Kód', typ: 'text', povinne: true, placeholder: '111' },
            { klic: 'poradi', nazev: 'Pořadí', typ: 'cislo' },
            { klic: 'nazev', nazev: 'Název', typ: 'text', povinne: true, siroke: true },
        ]} />;
}

export function StatniPrislusnosti(p) {
    return <CiselnikEditor {...p} endpoint="/api/konfigurace/statni-prislusnosti" nazevPolozky="příslušnost" razeni={poradi}
        titulek={(r) => `${r.kod} · ${r.nazev}`}
        pole={[
            { klic: 'kod', nazev: 'Kód (ISO)', typ: 'text', povinne: true, placeholder: 'CZ' },
            { klic: 'poradi', nazev: 'Pořadí', typ: 'cislo' },
            { klic: 'nazev', nazev: 'Název', typ: 'text', povinne: true, siroke: true },
        ]} />;
}

export function KategorieSmernic(p) {
    return <CiselnikEditor {...p} endpoint="/api/konfigurace/smernice-kategorie" nazevPolozky="kategorii" razeni={poradi}
        titulek={(r) => r.nazev} meta={(r) => (r.kod ? `kód ${r.kod} — prefix čísla směrnice` : null)}
        pripravit={(f) => ({ ...f, kod: f.kod ? String(f.kod).toUpperCase() : null })}
        pole={[
            { klic: 'nazev', nazev: 'Název', typ: 'text', povinne: true, siroke: true },
            { klic: 'kod', nazev: 'Kód', typ: 'text', placeholder: 'BOZP', napoveda: 'Do 10 znaků, používá se v čísle směrnice.' },
            { klic: 'poradi', nazev: 'Pořadí', typ: 'cislo' },
        ]} />;
}

const MIME = [
    { value: 'application/pdf', label: 'PDF' },
    { value: 'image/jpeg', label: 'JPEG' },
    { value: 'image/png', label: 'PNG' },
    { value: 'application/msword', label: 'Word (DOC)' },
    { value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', label: 'Word (DOCX)' },
    { value: 'application/vnd.ms-excel', label: 'Excel (XLS)' },
    { value: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', label: 'Excel (XLSX)' },
];

export function TypyDokumentu(p) {
    const [uzivatele, setUzivatele] = useState([]);
    useEffect(() => { nactiJson('/api/uzivatele').then((d) => setUzivatele((Array.isArray(d) ? d : d.data || []).filter((u) => u.stav === 'aktivni'))); }, []);
    return <CiselnikEditor {...p} endpoint="/api/konfigurace/typy-dokumentu" nazevPolozky="typ dokumentu" razeni={poradi}
        vychozi={{ aktivni: true, zobrazit_v_prehledu: true }}
        meta={(r) => [
            r.vyzaduje_podpis_zamestnanec && 'podpis zaměstnance',
            r.vyzaduje_podpis_zamestnavatel && (r.podpis_zamestnavatel_auto ? 'podpis firmy automaticky' : 'podpis firmy'),
            r.povolene_mime?.length ? r.povolene_mime.map((m) => MIME.find((o) => o.value === m)?.label || m).join(', ') : 'všechny soubory',
            r.aktivni === false && 'neaktivní',
        ].filter(Boolean).join(' · ')}
        pripravit={(f) => ({ ...f, poradi: Number(f.poradi) || 0, povolene_mime: f.povolene_mime?.length ? f.povolene_mime : null, podpis_zamestnavatel_uzivatel_id: f.podpis_zamestnavatel_uzivatel_id || null })}
        pole={[
            { klic: 'nazev', nazev: 'Název', typ: 'text', povinne: true },
            { klic: 'poradi', nazev: 'Pořadí', typ: 'cislo' },
            { klic: 'aktivni', nazev: 'Aktivní', typ: 'prepinac' },
            { klic: 'zobrazit_v_prehledu', nazev: 'Ukazovat v přehledu zaměstnance', typ: 'prepinac' },
            { klic: 'vyzaduje_podpis_zamestnanec', nazev: 'Podepisuje zaměstnanec', typ: 'prepinac' },
            { klic: 'vyzaduje_podpis_zamestnavatel', nazev: 'Podepisuje zaměstnavatel', typ: 'prepinac' },
            { klic: 'podpis_zamestnavatel_auto', nazev: 'Podpis zaměstnavatele vložit automaticky (podpis a razítko ze Správy → Firma)', typ: 'prepinac', zobrazit: (f) => f.vyzaduje_podpis_zamestnavatel },
            { klic: 'podpis_zamestnavatel_uzivatel_id', nazev: 'Kdo podepisuje za firmu', typ: 'volba', zobrazit: (f) => f.vyzaduje_podpis_zamestnavatel && !f.podpis_zamestnavatel_auto,
                moznosti: uzivatele.map((u) => ({ value: u.id, label: u.cele_jmeno || `${u.jmeno} ${u.prijmeni}` })) },
            { klic: 'povolene_mime', nazev: 'Povolené soubory (nic = všechny)', typ: 'vice', moznosti: MIME, siroke: true },
        ]} />;
}

const VZORCE = [
    { value: 'smenova_pritomnost', label: 'Směnová přítomnost (%)' },
    { value: 'cert_ok', label: 'Certifikace OK (100/0)' },
    { value: 'hodiny_mesic', label: 'Hodiny v měsíci' },
];

export function KpiKatalog(p) {
    const [kariery, setKariery] = useState([]);
    useEffect(() => { nactiJson('/api/ciselniky/kariery').then((d) => setKariery(Array.isArray(d) ? d : [])); }, []);
    return <CiselnikEditor {...p} endpoint="/api/bonus/kpi" nazevPolozky="KPI" razeni={poradi}
        vychozi={{ typ: 'manual', threshold_pct: 80, aktivni: true }}
        popis="KPI pro měsíční bonus. Bonus se vyplatí při splnění aspoň na prahu; KPI bez kariéry platí pro všechny."
        meta={(r) => [r.kariera?.nazev || 'všechny kariéry', r.typ === 'auto' ? `automaticky · ${VZORCE.find((v) => v.value === r.auto_formula)?.label || r.auto_formula}` : 'ručně',
            `práh ${r.threshold_pct} %`, `max ${kc(r.max_bonus_kc)}`, r.aktivni === false && 'neaktivní'].filter(Boolean).join(' · ')}
        pripravit={(f) => ({ ...f, kariera_id: f.kariera_id ? Number(f.kariera_id) : null, auto_formula: f.typ === 'auto' ? f.auto_formula : null })}
        pole={[
            { klic: 'nazev', nazev: 'Název', typ: 'text', povinne: true },
            { klic: 'kariera_id', nazev: 'Kariéra', typ: 'volba', moznosti: kariery.map((k) => ({ value: k.id, label: `${k.ikona || ''} ${k.nazev}`.trim() })) },
            { klic: 'typ', nazev: 'Vyhodnocení', typ: 'volba', povinne: true, moznosti: [{ value: 'manual', label: 'Ručně (vedoucí)' }, { value: 'auto', label: 'Automaticky' }] },
            { klic: 'auto_formula', nazev: 'Vzorec', typ: 'volba', povinne: true, moznosti: VZORCE, zobrazit: (f) => f.typ === 'auto' },
            { klic: 'max_bonus_kc', nazev: 'Maximální bonus', typ: 'cislo', povinne: true, pripona: 'Kč' },
            { klic: 'threshold_pct', nazev: 'Práh splnění', typ: 'cislo', povinne: true, pripona: '%' },
            { klic: 'aktivni', nazev: 'Aktivní', typ: 'prepinac' },
            { klic: 'popis', nazev: 'Popis', typ: 'text', siroke: true },
        ]} />;
}

/** Provozní náklady — jen vybrané kódy (⚙ Receptář: kalkulace, ⚙ Rozvoz: pravidla). Jen hodnoty, kódy zakládá migrace. */
export function ProvozniNaklady({ kody, popis, ...p }) {
    const sada = new Set(kody);
    return <CiselnikEditor {...p} endpoint="/api/konfigurace/provozni-naklady" muzePridat={false} muzeMazat={false} nazevPolozky="hodnotu"
        popis={popis} filtr={(r) => sada.has(r.kod)} razeni={(a, b) => kody.indexOf(a.kod) - kody.indexOf(b.kod)}
        meta={(r) => `${Number(r.hodnota).toLocaleString('cs-CZ')} ${r.jednotka || ''}`.trim()}
        pole={[{ klic: 'hodnota', nazev: 'Hodnota', typ: 'cislo', krok: 'any', povinne: true, pripona: (f, radek) => radek.jednotka || '' }]} />;
}

/** Dovednosti po sekcích — sekce i dovednosti stejným editorem. */
export function Dovednosti({ isDark, barva, upravuje = true }) {
    const [sekce, setSekce] = useState(null);
    const nacti = useCallback(() => nactiJson('/api/dovednosti/sekce').then((d) => setSekce(Array.isArray(d) ? d : [])), []);
    useEffect(() => { nacti(); }, [nacti]);
    const tlumene = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    return (
        <div>
            <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: tlumene, margin: '0 0 4px' }}>Sekce</p>
            <CiselnikEditor isDark={isDark} barva={barva} upravuje={upravuje} endpoint="/api/dovednosti/sekce" data={sekce} onZmena={nacti}
                nazevPolozky="sekci (i s jejími dovednostmi)" razeni={poradi} titulek={(r) => `${r.ikona || ''} ${r.nazev}`.trim()}
                meta={(r) => `${(r.dovednosti || []).length} dovedností`}
                pole={[
                    { klic: 'nazev', nazev: 'Název', typ: 'text', povinne: true },
                    { klic: 'ikona', nazev: 'Ikona (emoji)', typ: 'text', placeholder: '🍕' },
                    { klic: 'poradi', nazev: 'Pořadí', typ: 'cislo' },
                ]} />
            {(sekce || []).slice().sort(poradi).map((s) => (
                <div key={s.id} style={{ marginTop: 18 }}>
                    <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: tlumene, margin: '0 0 4px' }}>{s.ikona} {s.nazev}</p>
                    <CiselnikEditor isDark={isDark} barva={barva} upravuje={upravuje} endpoint="/api/dovednosti" data={s.dovednosti || []} onZmena={nacti}
                        nazevPolozky="dovednost" razeni={poradi} vychozi={{ sekce_id: s.id }} prazdne="Sekce zatím nemá dovednosti."
                        titulek={(r) => r.nazev} meta={(r) => [r.kod, r.popis].filter(Boolean).join(' · ')}
                        pole={[
                            { klic: 'nazev', nazev: 'Název', typ: 'text', povinne: true },
                            { klic: 'kod', nazev: 'Kód', typ: 'text', povinne: true },
                            { klic: 'popis', nazev: 'Popis', typ: 'text', siroke: true },
                            { klic: 'poradi', nazev: 'Pořadí', typ: 'cislo' },
                        ]} />
                </div>
            ))}
        </div>
    );
}

export function Certifikaty(p) {
    return <CiselnikEditor {...p} endpoint="/api/certifikace" nazevPolozky="certifikát"
        titulek={(r) => `${r.odznak || ''} ${r.nazev}`.trim()} meta={(r) => `${r.kod} · platnost ${r.platnost_mesice} měsíců`}
        popis="Typy certifikátů (HACCP, BOZP…). Které kariéra vyžaduje, se nastavuje v Lidé → Kariéry."
        vychozi={{ platnost_mesice: 12, odznak: '📜' }}
        pripravit={(f) => ({ ...f, kod: String(f.kod || '').toUpperCase() })}
        pole={[
            { klic: 'nazev', nazev: 'Název', typ: 'text', povinne: true },
            { klic: 'kod', nazev: 'Kód', typ: 'text', povinne: true, placeholder: 'HACCP' },
            { klic: 'platnost_mesice', nazev: 'Platnost', typ: 'cislo', povinne: true, pripona: 'měsíců' },
            { klic: 'odznak', nazev: 'Odznak (emoji)', typ: 'text', placeholder: '📜' },
        ]} />;
}
